/**
 * Kanban.js — Vue principale Planning d'Actions
 * Refonte complète — Design System SEIZO
 *
 * Charte : Sofia Pro / Nunito | #002335 | #00B0FF | #F4F7F9
 * - Header fixe : titre + toggle vue + actions
 * - Filtres chips scrollables
 * - Colonnes avec cartes top class (dates inline, priorité, avatar, progression)
 * - FAB création rapide
 * - WebSocket KanbanRefreshEvent + StatutsRefreshEvent
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useStores } from '../../../stores/Context';
import { observer } from 'mobx-react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import axios from 'axios';
import ModalPrimary from '../../../boiteAOutils/ModalPrimary';
import IconeStatut from './Icone/IconeStatut';
import styles from './Kanban.module.scss';
import { getPlanningPriority } from './planningPriority';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatEcheance = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.ceil((d - today) / 86400000);
    if (diff < 0) return { label: `${Math.abs(diff)}j retard`, color: '#F82121', urgent: true };
    if (diff === 0) return { label: "Aujourd'hui", color: '#F8D541', urgent: true };
    if (diff === 1) return { label: 'Demain', color: '#FF9436', urgent: false };
    if (diff <= 7) return { label: `${diff}j`, color: '#00B0FF', urgent: false };
    return {
        label: d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
        color: '#A8B0B3',
        urgent: false,
    };
};

// ── Icônes SVG inline ─────────────────────────────────────────────────────────

const IcoPlus = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);

const IcoSettings = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
);

const IcoCalendar = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
);

const IcoKanban = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="11" rx="1"/>
    </svg>
);

const IcoMore = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
    </svg>
);

const IcoInbox = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
        <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/>
    </svg>
);

const IcoArchive = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/>
        <line x1="10" y1="12" x2="14" y2="12"/>
    </svg>
);

// ── Nouveau Statut (colonne) ─────────────────────────────────────────────────

const COULEURS_PRESET = [
    '#00B0FF', '#66BE63', '#F8D541', '#FF9436', '#F82121',
    '#9B59B6', '#1ABC9C', '#E67E22', '#3498DB', '#95A5A6',
];

const NouveauStatutBtn = observer(() => {
    const { PlanningActionsStore } = useStores();
    const [open, setOpen] = useState(false);
    const [libelle, setLibelle] = useState('');
    const [couleur, setCouleur] = useState(COULEURS_PRESET[0]);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (open && inputRef.current) inputRef.current.focus();
    }, [open]);

    const handleSave = async () => {
        if (!libelle.trim()) { setErr('Nom requis'); return; }
        setSaving(true);
        setErr('');
        try {
            const newStatuts = [
                ...PlanningActionsStore.statuts,
                { libelle: libelle.trim(), icone: 'Rond', couleur },
            ];
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/PA/saveParametrage/${PlanningActionsStore.idPlanning}`,
                { nomPlanning: PlanningActionsStore.structure?.nom, statuts: newStatuts },
                { headers: { Authorization: `Bearer ${PlanningActionsStore._token}` } }
            );
            await PlanningActionsStore.recupStatuts();
            setLibelle('');
            setCouleur(COULEURS_PRESET[0]);
            setOpen(false);
        } catch (e) {
            setErr('Erreur lors de la création');
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return (
            <button className={styles.addStatutBtn} onClick={() => setOpen(true)} title="Ajouter un statut">
                <IcoPlus />
                <span>Nouveau statut</span>
            </button>
        );
    }

    return (
        <div className={styles.addStatutForm}>
            <input
                ref={inputRef}
                className={styles.addStatutInput}
                placeholder="Nom du statut…"
                value={libelle}
                onChange={e => { setLibelle(e.target.value); setErr(''); }}
                onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setOpen(false); }}
                maxLength={40}
            />
            <div className={styles.addStatutCouleurs}>
                {COULEURS_PRESET.map(c => (
                    <button
                        key={c}
                        className={`${styles.couleurDot} ${couleur === c ? styles.couleurDotActive : ''}`}
                        style={{ background: c }}
                        onClick={() => setCouleur(c)}
                        title={c}
                    />
                ))}
            </div>
            {err && <div className={styles.addStatutErr}>{err}</div>}
            <div className={styles.addStatutActions}>
                <button className={styles.addStatutCancel} onClick={() => { setOpen(false); setErr(''); setLibelle(''); }}>
                    Annuler
                </button>
                <button className={styles.addStatutSave} onClick={handleSave} disabled={saving}>
                    {saving ? '…' : 'Créer'}
                </button>
            </div>
        </div>
    );
});

// ── Carte Tâche ───────────────────────────────────────────────────────────────

const TacheCard = observer(({ tache, onEdit, onDuplicate, onArchive, onSendTo, totemsDestinataires, targetsLoading, canTransmit = true }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [sendToOpen, setSendToOpen] = useState(false);
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
    const [menuPosition, setMenuPosition] = useState(null);
    const menuRef = useRef(null);
    const menuPanelRef = useRef(null);

    const dateInfo = formatEcheance(tache.date_estimee);
    const prioColor = getPlanningPriority(tache.priorite).color;

    useEffect(() => {
        const handler = (e) => {
            const targetIsInTrigger = menuRef.current?.contains(e.target);
            const targetIsInMenu = menuPanelRef.current?.contains(e.target);
            if (!targetIsInTrigger && !targetIsInMenu) {
                setMenuOpen(false);
                setSendToOpen(false);
            }
        };
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setMenuOpen(false);
                setSendToOpen(false);
            }
        };
        if (menuOpen) {
            document.addEventListener('mousedown', handler);
            document.addEventListener('keydown', handleEscape);
        }
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [menuOpen]);

    const toggleMenu = (event) => {
        event.stopPropagation();
        if (menuOpen) {
            setMenuOpen(false);
            setSendToOpen(false);
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const menuWidth = 184;
        setMenuPosition({
            top: Math.min(rect.bottom + 6, window.innerHeight - 12),
            left: Math.max(12, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 12)),
        });
        setSendToOpen(false);
        setMenuOpen(true);
    };

    return (
        <>
            <div
                className={`${styles.card} ${dateInfo?.urgent ? styles.cardUrgent : ''}`}
                onClick={() => onEdit(tache)}
                style={{ borderLeftColor: tache.statut?.couleur || '#DBE3E7' }}
            >
                {/* Titre */}
                <div className={styles.cardTitle}>{tache.titre}</div>

                {/* Description (2 lignes max) */}
                {tache.description && (
                    <div className={styles.cardDesc}>{tache.description}</div>
                )}

                {/* Meta row */}
                <div className={styles.cardMeta}>
                    {/* Statut icône */}
                    {tache.statut && (
                        <span className={styles.cardStatutIcon}>
                            <IconeStatut icone={tache.statut.icone} couleur={tache.statut.couleur} />
                        </span>
                    )}

                    {/* Priorité dot */}
                    <span className={styles.cardPrioDot} style={{ background: prioColor }} />

                    {/* Date échéance */}
                    {dateInfo && (
                        <span className={styles.cardDate} style={{ color: dateInfo.color }}>
                            <IcoCalendar />
                            {dateInfo.label}
                        </span>
                    )}

                    {/* Responsable avatar */}
                    {tache.responsable && (
                        <span
                            className={styles.cardAvatar}
                            title={`${tache.responsable.prenom} ${tache.responsable.nom}`}
                        >
                            {tache.responsable.prenom?.[0]}{tache.responsable.nom?.[0]}
                        </span>
                    )}

                    {/* Avancement */}
                    {tache.pourcentage > 0 && (
                        <span className={styles.cardPct}>{tache.pourcentage}%</span>
                    )}

                    <span style={{ flex: 1 }} />

                    {/* Menu ⋯ */}
                    <div className={styles.cardMenuWrapper} ref={menuRef}>
                        <button
                            className={styles.cardMenuBtn}
                            type="button"
                            aria-label={`Actions pour ${tache.titre}`}
                            aria-expanded={menuOpen}
                            onClick={toggleMenu}
                        >
                            <IcoMore />
                        </button>
                    </div>
                </div>


            </div>

            {showArchiveConfirm && (
                <ModalPrimary
                    textTitle=""
                    textComment="Êtes-vous sûr de vouloir archiver cette tâche ?"
                    textBtnValidate="Archiver"
                    textBtnCancel="Annuler"
                    onValidate={() => { onArchive(tache.id); setShowArchiveConfirm(false); }}
                    onCancel={() => setShowArchiveConfirm(false)}
                />
            )}

            {menuOpen && menuPosition && createPortal(
                <div
                    className={styles.cardMenu}
                    ref={menuPanelRef}
                    style={{ top: menuPosition.top, left: menuPosition.left, right: 'auto' }}
                    role="menu"
                    aria-label={`Actions de ${tache.titre}`}
                    onClick={e => e.stopPropagation()}
                >
                    <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onEdit(tache); }}>
                        Modifier
                    </button>
                    <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onDuplicate(tache.id); }}>
                        Dupliquer
                    </button>
                    {canTransmit && <>
                        <div className={styles.cardMenuSeparator} />
                        <button type="button" role="menuitem" onClick={() => setSendToOpen(v => !v)}>
                            Transmettre à un Totem ›
                        </button>
                        {sendToOpen && (
                            <div className={styles.cardSubMenu}>
                                {targetsLoading ? (
                                    <span className={styles.cardMenuEmpty}>Chargement des Totems…</span>
                                ) : totemsDestinataires?.length ? totemsDestinataires.map(totem => (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        key={totem.id_totem}
                                        onClick={() => { setMenuOpen(false); setSendToOpen(false); onSendTo(tache, totem.id_totem); }}
                                    >
                                        {totem.tot_nom || 'Totem sans nom'}
                                    </button>
                                )) : (
                                    <span className={styles.cardMenuEmpty}>Aucun Totem disponible</span>
                                )}
                            </div>
                        )}
                    </>}
                    <div className={styles.cardMenuSeparator} />
                    <button
                        type="button"
                        role="menuitem"
                        className={styles.cardMenuDanger}
                        onClick={() => { setMenuOpen(false); setShowArchiveConfirm(true); }}
                    >
                        <IcoArchive /> Archiver
                    </button>
                </div>,
                document.body
            )}
        </>
    );
});

// ── Colonne Kanban ────────────────────────────────────────────────────────────

const KanbanColonne = observer(({ statut, filtres, onEdit, onDuplicate, onArchive, onSendTo, totemsDestinataires, targetsLoading, sentLaneId, onAddTask, onAcceptReception }) => {
    const { PlanningActionsStore } = useStores();

    const taches = PlanningActionsStore.taches.filter(t => {
        if (sentLaneId && String(t.ligne_id) === String(sentLaneId)) return false;
        if (t.statut?.id !== statut.id) return false;
        if (filtres.priorite && Number(t.priorite) !== Number(filtres.priorite)) return false;
        if (filtres.responsable_id && t.responsable?.id !== parseInt(filtres.responsable_id)) return false;
        if (filtres.urgentes && !t.is_urgente) return false;
        return true;
    });

    return (
        <div
            className={styles.colonne}
            data-statut-id={statut.id}
        >
            {/* Header colonne */}
            <div className={styles.colonneHeader}>
                <div className={styles.colonneHeaderLeft}>
                    <span className={styles.colonneBarre} style={{ background: statut.couleur }} />
                    <span className={styles.colonneNom}>{statut.libelle}</span>
                </div>
                <div className={styles.colonneHeaderRight}>
                    <span className={styles.colonneCount}>{taches.length}</span>
                    <button
                        className={styles.colonneAddBtn}
                        onClick={() => onAddTask(statut.id)}
                        title="Ajouter une tâche"
                    >
                        <IcoPlus />
                    </button>
                </div>
            </div>

            {/* Liste droppable */}
            <Droppable droppableId={`${statut.id}`}>
                {(provided, snapshot) => (
                    <div
                        className={`${styles.colonneListe} ${snapshot.isDraggingOver ? styles.colonneListeDragOver : ''}`}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                    >
                        {taches.map((tache, index) => (
                            <Draggable key={tache.id} draggableId={`${tache.id}`} index={index}>
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={{
                                            ...provided.draggableProps.style,
                                            opacity: snapshot.isDragging ? 0.85 : 1,
                                        }}
                                    >
                                        <TacheCard
                                            tache={tache}
                                            onEdit={onEdit}
                                            onDuplicate={onDuplicate}
                                            onArchive={onArchive}
                                            onSendTo={onSendTo}
                                            totemsDestinataires={totemsDestinataires}
                                            targetsLoading={targetsLoading}
                                        />
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}

                        {taches.length === 0 && !snapshot.isDraggingOver && (
                            <div className={styles.colonneVide}>
                                Glissez une tâche ici
                            </div>
                        )}
                    </div>
                )}
            </Droppable>
        </div>
    );
});

// ── Filtres chips ─────────────────────────────────────────────────────────────

const FiltresBar = ({ filtres, onChange, membres }) => {
    const chips = [
        { key: 'urgentes',  label: '🔥 Urgentes',  value: true },
        { key: 'priorite',  label: '🔴 Critique',   value: 4 },
        { key: 'priorite',  label: '🟠 Haute',      value: 3 },
        { key: 'priorite',  label: '🟡 Normale',    value: 2 },
    ];

    const isActive = (key, value) => filtres[key] === value;
    const toggle = (key, value) => onChange(key, isActive(key, value) ? null : value);
    const hasActive = Object.values(filtres).some(v => v);

    return (
        <div className={styles.filtresBar}>
            {chips.map((c, i) => (
                <button
                    key={i}
                    className={`${styles.filtreChip} ${isActive(c.key, c.value) ? styles.filtreChipActive : ''}`}
                    onClick={() => toggle(c.key, c.value)}
                >
                    {c.label}
                </button>
            ))}
            {membres?.length > 0 && (
                <select
                    className={styles.filtreSelect}
                    value={filtres.responsable_id || ''}
                    onChange={e => onChange('responsable_id', e.target.value || null)}
                >
                    <option value="">👤 Tous</option>
                    {membres.map(m => (
                        <option key={m.id} value={m.id}>{m.prenom} {m.nom}</option>
                    ))}
                </select>
            )}
            {hasActive && (
                <button
                    className={styles.filtreReset}
                    onClick={() => { onChange('urgentes', null); onChange('priorite', null); onChange('responsable_id', null); }}
                >
                    ✕ Effacer
                </button>
            )}
        </div>
    );
};

// ── Composant principal ───────────────────────────────────────────────────────


// ── Titre inline éditable ─────────────────────────────────────────────────────
const PlanningTitleEditable = observer(() => {
    const { PlanningActionsStore } = useStores();
    const [editing, setEditing] = React.useState(false);
    const [value, setValue] = React.useState('');
    const inputRef = React.useRef(null);
    const nom = PlanningActionsStore.structure?.nom || '';

    const handleClick = () => {
        setValue(nom);
        setEditing(true);
        setTimeout(() => inputRef.current?.select(), 0);
    };

    const handleSave = async () => {
        const trimmed = value.trim();
        if (!trimmed || trimmed === nom) { setEditing(false); return; }
        try {
            await PlanningActionsStore.renommerPlanning(trimmed);
        } catch (_) {}
        setEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSave();
        if (e.key === 'Escape') setEditing(false);
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                value={value}
                onChange={e => setValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                className={styles.headerTitleInput}
                style={{
                    font: 'inherit',
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    color: 'inherit',
                    background: 'rgba(0,176,255,0.08)',
                    border: '1.5px solid #00B0FF',
                    borderRadius: 6,
                    padding: '2px 8px',
                    outline: 'none',
                    minWidth: 120,
                    maxWidth: 320,
                }}
            />
        );
    }

    return (
        <h1
            className={styles.headerTitle}
            onClick={handleClick}
            title="Cliquer pour renommer"
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
        >
            {nom}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#00B0FF" strokeWidth="2" strokeLinecap="round" style={{ opacity: 0.7, flexShrink: 0 }}>
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
        </h1>
    );
});

const Kanban = observer(() => {
    const { PlanningActionsStore } = useStores();
    const { statuts, structure, intertotemTargets, intertotemActionsLoading } = PlanningActionsStore;
    const [filtres, setFiltres] = useState({});
    const sentLane = structure?.lignes?.find(ligne => ligne.libelle === 'Actions envoyées') || null;

    const handleFiltreChange = (key, value) => setFiltres(prev => ({ ...prev, [key]: value }));

    // ── WebSocket ─────────────────────────────────────────────────────────────
    window.Pusher = Pusher;
    useEffect(() => {
        if (!PlanningActionsStore.idPlanning) return;
        const echo = new Echo({
            broadcaster: 'pusher',
            key: process.env.REACT_APP_WS_KEY,
            secret: process.env.REACT_APP_WS_SECRET,
            wsHost: process.env.REACT_APP_WS_URL,
            wsPort: 6001,
            forceTLS: true,
            encrypted: true,
            disableStats: true,
            enabledTransports: ['ws', 'wss'],
            cluster: 'eu',
        });
        echo.channel(PlanningActionsStore.idPlanning + '-Planning_kanbanRefreshChannel')
            .listen('PlanningActions\\KanbanRefreshEvent', (res) => {
                const { tache, typeAction } = res;
                PlanningActionsStore.updateLocalTacheStatut(tache.id, tache.statut_id)
                    .then(() => {
                        if (typeAction === 'updateDatas') PlanningActionsStore.updateLocalTacheDatas(tache);
                        else if (typeAction === 'deleteDatas') PlanningActionsStore.deleteLocalTache(tache.id);
                    });
            });
        echo.channel(PlanningActionsStore.idPlanning + '-Planning_statutsRefreshChannel')
            .listen('PlanningActions\\StatutsRefreshEvent', () => PlanningActionsStore.recupStatuts());
        return () => echo.disconnect();
    }, [PlanningActionsStore.idPlanning]);

    // ── Drag & Drop ───────────────────────────────────────────────────────────
    const onDragEnd = (result) => {
        if (!result.destination) return;
        PlanningActionsStore.moveTacheKanban(result.draggableId, result.destination.droppableId);
    };

    // ── Actions tâche ─────────────────────────────────────────────────────────
    const handleEdit = (tache) => {
        PlanningActionsStore.openModalFormAction(tache.colonne_id, tache.sous_colonne_id, tache.ligne_id, tache.id);
    };
    const handleDuplicate = (id) => PlanningActionsStore.dupliquerTache(id);
    const handleArchive = (id) => {
        PlanningActionsStore.archiveTacheToDatabase(id).then(() => PlanningActionsStore.archiveTacheToLocal(id));
    };
    const handleSendTo = (tache, totemId) => PlanningActionsStore.envoyerTacheVersTotem(totemId, tache)
        .catch(error => console.error('Erreur transmission action inter-Totems:', error));

    if (!structure) {
        return (
            <div className={styles.loadingState}>
                <div className={styles.loadingSpinner} />
                <span>Chargement du planning…</span>
            </div>
        );
    }

    return (
        <div className={styles.kanbanRoot}>

            {/* ── Titre du planning ──────────────────────────────────────────────── */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <span className={styles.headerDot} />
                    <PlanningTitleEditable />
                </div>
            </div>

            {/* ── Filtres ─────────────────────────────────────────────────────────────────── */}
            <FiltresBar
                filtres={filtres}
                onChange={handleFiltreChange}
                membres={PlanningActionsStore.membres}
            />

            {/* ── Board ─────────────────────────────────────────────────────────────────── */}
            <div
                className={styles.board}
                onDragOver={e => {
                    if (e.dataTransfer.types.includes('application/reception-tache')) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                    }
                }}
                onDrop={e => {
                    const tacheId = e.dataTransfer.getData('application/reception-tache');
                    if (tacheId) {
                        e.preventDefault();
                        // Find which column was hovered using data attribute
                        const colonneEl = e.target.closest('[data-statut-id]');
                        const statutId = colonneEl ? colonneEl.getAttribute('data-statut-id') : (statuts[0]?.id || null);
                        if (statutId) {
                            PlanningActionsStore.accepterTacheReception(tacheId, statutId);
                        }
                    }
                }}
            >
                <DragDropContext onDragEnd={onDragEnd}>
                    {statuts.map((statut) => (
                        <KanbanColonne
                            key={statut.id}
                            statut={statut}
                            filtres={filtres}
                            onEdit={handleEdit}
                            onDuplicate={handleDuplicate}
                            onArchive={handleArchive}
                            onSendTo={handleSendTo}
                            totemsDestinataires={intertotemTargets}
                            targetsLoading={intertotemActionsLoading}
                            sentLaneId={sentLane?.id}
                            onAddTask={() => PlanningActionsStore.openModalFormAction(null, null, null, null)}
                            onAcceptReception={(tacheId, statutId) => PlanningActionsStore.accepterTacheReception(tacheId, statutId)}
                        />
                    ))}
                </DragDropContext>
                {/* Bouton + Nouveau statut */}
                <NouveauStatutBtn />
            </div>

            {/* ── FAB mobile ──────────────────────────────────────────────────────── */}
            <button
                className={styles.fab}
                onClick={() => PlanningActionsStore.openModalFormAction(null, null, null, null)}
                title="Nouvelle tâche"
            >
                <IcoPlus />
            </button>
        </div>
    );
});
export default Kanban;
